import { useEffect, useState } from 'react';
import { fetchDocumentHistory, type HistoryEntryDto } from '../../api/approval';
import './ApprovalHistory.css';

/**
 * 문서 이력 (2026-09-04 · 판 P6 · 결정 ⑧).
 *
 * ## ★★★ 왜 필요한가
 *
 * 지금 화면은 **「지금 어디까지 갔나」**만 말한다(결재란·단계). 「누가 언제 무엇을 했나」는
 * 감사 로그에만 있어서 **아무도 못 본다** — 「이 문서 왜 반려됐지」를 물으면 답할 자리가 없었다.
 *
 * ## ★★★ 대결이 여기서 드러난다
 *
 * 위임으로 **대신 누른** 경우 감사에 `onBehalfOfPrincipalId` 가 남는다. 결재란에는
 * 원래 결재자만 보이므로 「실제로 누른 사람」은 지금까지 어디에도 안 보였다.
 * 이 탭이 **칸을 하나도 안 늘리고** 그것을 말한다.
 *
 * ## ★ 열 때만 읽는다
 *
 * 문서를 열 때마다 감사 표를 훑을 이유가 없다 — 탭을 눌렀을 때 부른다.
 * 옛 서버(404)면 **조용히 접힌다**(문서 보기를 방해하지 않는다).
 */

/** 종류 → 사람이 읽는 말. **여기 없는 종류는 그대로 적는다**(숨기지 않는다). */
const LABEL: Record<string, string> = {
  'approval.document.drafted': '기안',
  'approval.document.submitted': '상신',
  'approval.document.recalled': '회수',
  'approval.line.approved': '승인',
  'approval.line.rejected': '반려',
  /*
    ⛔ 합의 이력 낱말(`agreed`/`disagreed`)을 지웠다 (2026-09-27 · 합의 기능 삭제).
      서버가 그 종류를 더는 남기지 않는다(#1774).
    ★ 위 규칙대로 **여기 «없는» 종류는 그대로 적는다** — 옛 감사 줄이 남아 있어도 «숨기지 않는다».
      그래서 지워도 과거 이력이 사라지지 않는다(종류 글자가 그대로 뜬다).
  */
};

/** 승인·반려는 색이 다르다 — 다만 **색만으로 뜻을 만들지 않는다**(낱말이 함께 있다). */
const TONE: Record<string, string> = {
  'approval.line.approved': 'is-ok',
  'approval.line.rejected': 'is-no',
  'approval.document.recalled': 'is-no',
  'approval.line.disagreed': 'is-no',
};

const when = (iso: string) => {
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

export function ApprovalHistory({ documentId }: { documentId: string }) {
  const [entries, setEntries] = useState<HistoryEntryDto[] | null>(null);
  const [state, setState] = useState<'loading' | 'ok' | 'folded'>('loading');

  useEffect(() => {
    let alive = true;
    setState('loading');
    fetchDocumentHistory(documentId)
      .then((e) => { if (alive) { setEntries(e); setState('ok'); } })
      // ★★ 옛 서버(404)·권한 어느 쪽이든 조용히 접는다 — 문서 보기를 방해하지 않는다.
      .catch(() => { if (alive) setState('folded'); });
    return () => { alive = false; };
  }, [documentId]);

  if (state === 'loading') return <p className="apphist__note">불러오는 중…</p>;
  if (state === 'folded') {
    return <p className="apphist__note">이력을 아직 볼 수 없습니다 — 서버가 이 기능을 갖추면 여기에 나타납니다.</p>;
  }
  if (!entries || entries.length === 0) {
    return <p className="apphist__note">남은 이력이 없습니다.</p>;
  }

  return (
    <ol className="apphist">
      {entries.map((e) => (
        <li key={e.id} className={TONE[e.eventType] ?? undefined}>
          <span className="apphist__when">{when(e.at)}</span>
          <span className="apphist__what">
            {LABEL[e.eventType] ?? e.eventType}
            {e.step !== null && <span className="apphist__step"> {e.step}단계</span>}
          </span>
          <span className="apphist__who">
            {e.actorName ?? '알 수 없음'}
            {/*
              ★★★ **대결** — 결재란에는 «원래 결재자» 만 보이므로 실제로 누른 사람은
                 지금까지 어디에도 없었다. 두 이름을 함께 적어야 뜻이 산다.
            */}
            {e.onBehalfOfName && (
              <span className="apphist__behalf"> — {e.onBehalfOfName} 대신</span>
            )}
          </span>
          {e.comment && <span className="apphist__comment">{e.comment}</span>}
        </li>
      ))}
    </ol>
  );
}
